<script setup lang="ts">
import { ArrowLeft, ArrowUpRight, BookOpenText, CalendarDays, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Clock3, ExternalLink, Tags } from "@lucide/vue";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import ContentRow from "../components/ContentRow.vue";
import DocumentRenderer from "../components/DocumentRenderer.vue";
import LoadingState from "../components/LoadingState.vue";
import { useContent } from "../composables/useContent";
import { useDocument } from "../composables/useDocument";
import { formatDate, getDisplayTags, getDisplayTitle, getPrimaryLabel, getRecordSection, isVpsReview, SECTION_LABELS } from "../lib/content";
import { applySeo, entrySeo } from "../lib/seo";
import type { HeadingBlock } from "../types/content";

const route = useRoute();
const { records, loading, error } = useContent();
const record = computed(() => records.value.find((item) => item.id === route.params.id));
const { document, loading: documentLoading, error: documentError } = useDocument(record);
const section = computed(() => record.value ? getRecordSection(record.value) : "knowledge");
const review = computed(() => Boolean(record.value && isVpsReview(record.value)));
const published = computed(() => document.value?.editorial_status === "published");
const backLink = computed(() => section.value === "tools" ? "/tools" : `/library?section=${section.value}`);
const displayTags = computed(() => record.value ? getDisplayTags(record.value, 8) : []);
const toc = computed(() => published.value ? document.value?.blocks.filter((block): block is HeadingBlock => block.type === "heading" && block.level === 2) ?? [] : []);
const articleContent = ref<HTMLElement | null>(null);
const activeHeadingId = ref("");
const readingProgress = ref(0);
const mobileTocOpen = ref(false);
let readingFrame = 0;
const activeHeadingIndex = computed(() => Math.max(0, toc.value.findIndex((item) => item.id === activeHeadingId.value)));
const activeHeading = computed(() => toc.value[activeHeadingIndex.value]);
const related = computed(() => {
  if (!record.value || !published.value) return [];
  const primaryLabel = getPrimaryLabel(record.value);
  return records.value.filter((item) => item.id !== record.value?.id && item.editorial_status === "published" && getRecordSection(item) === section.value && getPrimaryLabel(item) === primaryLabel).slice(0, 3);
});

function updateReadingState() {
  const content = articleContent.value;
  if (!content || !toc.value.length) return;

  const headerOffset = window.innerWidth <= 840 ? 118 : 92;
  const contentTop = window.scrollY + content.getBoundingClientRect().top;
  const scrollableDistance = Math.max(1, content.offsetHeight - window.innerHeight + headerOffset);
  readingProgress.value = Math.min(100, Math.max(0, ((window.scrollY - contentTop + headerOffset) / scrollableDistance) * 100));

  let current = toc.value[0]?.id ?? "";
  for (const item of toc.value) {
    const heading = window.document.getElementById(item.id);
    if (heading && heading.getBoundingClientRect().top <= headerOffset + 28) current = item.id;
    else break;
  }
  activeHeadingId.value = current;
}

function queueReadingUpdate() {
  if (readingFrame) return;
  readingFrame = window.requestAnimationFrame(() => {
    readingFrame = 0;
    updateReadingState();
  });
}

function navigateSection(offset: -1 | 1) {
  const target = toc.value[activeHeadingIndex.value + offset];
  if (!target) return;
  activeHeadingId.value = target.id;
  const heading = window.document.getElementById(target.id);
  if (heading) window.scrollTo({ top: window.scrollY + heading.getBoundingClientRect().top - 126, behavior: "smooth" });
}

watch([document, () => route.params.id], async () => {
  await nextTick();
  mobileTocOpen.value = false;
  activeHeadingId.value = toc.value[0]?.id ?? "";
  updateReadingState();
});

watch([record, document], ([nextRecord, nextDocument]) => {
  if (nextRecord && nextDocument && nextDocument.editorial_status === "published") {
    // The pre-rendered document has this data before JavaScript; keep it accurate after SPA navigation too.
    applySeo(entrySeo(nextRecord, nextDocument));
  }
}, { immediate: true });

onMounted(() => {
  window.addEventListener("scroll", queueReadingUpdate, { passive: true });
  window.addEventListener("resize", queueReadingUpdate);
  void nextTick(updateReadingState);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", queueReadingUpdate);
  window.removeEventListener("resize", queueReadingUpdate);
  if (readingFrame) window.cancelAnimationFrame(readingFrame);
});
</script>

<template>
  <LoadingState v-if="loading || error" :error="error" />
  <template v-else-if="record">
    <div v-if="published && toc.length" class="reading-progress" role="progressbar" aria-label="文章阅读进度" aria-valuemin="0" aria-valuemax="100" :aria-valuenow="Math.round(readingProgress)">
      <span :style="{ transform: `scaleX(${readingProgress / 100})` }" />
    </div>
    <header class="entry-head">
      <div class="reading-width entry-head-inner">
        <nav class="entry-breadcrumb" :class="`entry-breadcrumb-${section}`" aria-label="文章位置">
          <RouterLink :to="backLink"><ArrowLeft :size="16" />{{ SECTION_LABELS[section] }}</RouterLink>
          <ChevronRight :size="14" aria-hidden="true" />
          <span>{{ getPrimaryLabel(record) }}</span>
        </nav>
        <h1>{{ getDisplayTitle(record) }}</h1>
        <p v-if="published" class="entry-deck">{{ document?.description }}</p>
        <div class="entry-meta">
          <span v-if="record.published_at"><CalendarDays :size="15" />发布于 {{ formatDate(record.published_at) }}</span>
          <span v-if="published"><Clock3 :size="15" />更新于 {{ formatDate(document?.last_reviewed_at || null) }}</span>
          <span v-if="published && toc.length"><BookOpenText :size="15" />{{ toc.length }} 节</span>
          <span v-if="published" class="entry-meta-status"><CheckCircle2 :size="15" />已审核发布</span>
        </div>
      </div>
    </header>

    <section class="entry-band">
      <div class="page-width entry-layout">
        <article ref="articleContent" class="entry-content" aria-live="polite">
          <LoadingState v-if="documentLoading" label="正在加载内容状态" />
          <div v-else-if="documentError" class="document-error" role="alert"><strong>内容暂时无法读取</strong><p>{{ documentError }}</p></div>
          <template v-else-if="document">
            <div v-if="published && toc.length" class="article-toc-mobile">
              <div class="article-section-bar">
                <button type="button" class="article-toc-toggle" :aria-expanded="mobileTocOpen" aria-controls="mobile-article-toc" :aria-label="mobileTocOpen ? '收起本文目录' : '展开本文目录'" :title="mobileTocOpen ? '收起本文目录' : '展开本文目录'" @click="mobileTocOpen = !mobileTocOpen">
                  <BookOpenText :size="18" aria-hidden="true" /><ChevronDown :size="14" aria-hidden="true" />
                </button>
                <div class="article-section-current" aria-live="polite">
                  <small>阅读位置 · {{ String(activeHeadingIndex + 1).padStart(2, "0") }} / {{ String(toc.length).padStart(2, "0") }}</small>
                  <strong>{{ activeHeading?.text }}</strong>
                </div>
                <div class="article-section-actions">
                  <button type="button" :disabled="activeHeadingIndex === 0" aria-label="上一节" title="上一节" @click="navigateSection(-1)"><ChevronLeft :size="18" aria-hidden="true" /></button>
                  <button type="button" :disabled="activeHeadingIndex === toc.length - 1" aria-label="下一节" title="下一节" @click="navigateSection(1)"><ChevronRight :size="18" aria-hidden="true" /></button>
                </div>
              </div>
              <nav v-if="mobileTocOpen" id="mobile-article-toc" aria-label="移动端本文目录"><a v-for="item in toc" :key="item.id" :class="{ active: activeHeadingId === item.id }" :aria-current="activeHeadingId === item.id ? 'location' : undefined" :href="`#${item.id}`" @click="mobileTocOpen = false">{{ item.text }}</a></nav>
            </div>
            <DocumentRenderer v-if="published" :document="document" />

            <a v-if="published && document.official_link?.status === 'verified'" class="official-action" :href="document.official_link.url" target="_blank" rel="noopener noreferrer">
              <span><small>已验证官方入口 · {{ formatDate(document.official_link.checked_at) }}</small><strong>{{ document.official_link.label }}</strong></span><ExternalLink :size="19" />
            </a>

            <div v-if="published && document.references?.length" class="document-references">
              <h2>官方参考</h2>
              <ul><li v-for="reference in document.references" :key="reference.url"><a :href="reference.url" target="_blank" rel="noopener noreferrer">{{ reference.label }} <ArrowUpRight :size="15" /></a></li></ul>
            </div>

            <div v-if="displayTags.length" class="entry-tags">
              <h2><Tags :size="18" /> 分类与标签</h2>
              <div class="tag-list"><RouterLink v-for="tag in displayTags" :key="tag" :to="{ name: 'library', query: { section, category: tag } }">{{ tag }}</RouterLink></div>
            </div>

            <div v-if="review && record.source_url" class="source-disclosure">
              <div><strong>原始来源</strong><p>{{ published ? "来源用于核对历史测试语境，不代表当前价格、配置或可用性。" : "可先查看原始文章；VPScope 的深度审核与独立评价尚未完成。" }}</p></div>
              <a :href="record.source_url" target="_blank" rel="noopener noreferrer">查看原始文章 <ArrowUpRight :size="17" /></a>
            </div>
          </template>
        </article>

        <aside class="entry-aside">
          <nav v-if="toc.length" class="article-toc" aria-label="本文目录">
            <strong><span><BookOpenText :size="17" /> 本文目录</span><small>{{ toc.length }} 节</small></strong>
            <a v-for="item in toc" :key="item.id" :class="{ active: activeHeadingId === item.id }" :aria-current="activeHeadingId === item.id ? 'location' : undefined" :href="`#${item.id}`">{{ item.text }}</a>
          </nav>
          <dl>
            <div><dt>编辑状态</dt><dd>已审核发布</dd></div>
            <div><dt>编辑修订</dt><dd>{{ document?.editor_revision || 0 }}</dd></div>
            <div><dt>最近审核</dt><dd>{{ formatDate(document?.last_reviewed_at || null) }}</dd></div>
            <div><dt>稳定编号</dt><dd class="record-id">{{ record.id }}</dd></div>
          </dl>
        </aside>
      </div>
    </section>

    <section v-if="related.length" class="related-band">
      <div class="page-width"><div class="section-heading"><div><h2>继续阅读</h2><p>仅展示已完成审核的相关内容。</p></div></div><div class="content-list"><ContentRow v-for="item in related" :key="item.id" :record="item" /></div></div>
    </section>
  </template>
  <section v-else class="not-found"><div class="page-width"><strong>未找到该内容</strong><RouterLink to="/library">返回知识库</RouterLink></div></section>
</template>
